<template>
  <div class="cv">
    <div class="cv__bar">
      <button type="button" class="cv__nav" :title="$t('taskDeskPrev')" @click="shift(-1)">
        <v-icon small>{{ $vuetify.rtl ? 'mdi-chevron-right' : 'mdi-chevron-left' }}</v-icon>
      </button>
      <button type="button" class="cv__nav" :title="$t('taskDeskNext')" @click="shift(1)">
        <v-icon small>{{ $vuetify.rtl ? 'mdi-chevron-left' : 'mdi-chevron-right' }}</v-icon>
      </button>
      <span class="cv__title">{{ title }}</span>
      <button type="button" class="cv__chip" @click="goToday">{{ $t('taskDeskToday') }}</button>
      <v-spacer />
      <span class="cv__legend"><i class="cv__legend-dot" /> {{ $t('taskDeskOverdue') }}</span>
      <span v-if="unscheduled" class="cv__muted">{{ $t('taskDeskUnscheduled') }}: {{ unscheduled }}</span>
    </div>

    <div class="cv__week">
      <span v-for="(n, i) in weekdays" :key="i">{{ n }}</span>
    </div>

    <div class="cv__grid" :style="{ gridTemplateRows: 'repeat(' + weeks.length + ', minmax(0, 1fr))' }">
      <template v-for="week in weeks">
        <div
          v-for="d in week"
          :key="d.key"
          class="cv__cell"
          :class="{ 'cv__cell--out': !d.inMonth, 'cv__cell--today': d.isToday, 'cv__cell--we': d.weekend }"
        >
          <div class="cv__cell-head">
            <span class="cv__num">{{ d.label }}</span>
          </div>
          <div class="cv__events">
            <button
              v-for="t in (byDay[d.key] || []).slice(0, maxPerCell)"
              :key="t.key"
              type="button"
              class="cv__ev"
              :class="{ 'cv__ev--done': t.done, 'cv__ev--late': isLate(t) }"
              :style="{ '--ec': t.col.color || '#90a4ae' }"
              :title="t.el.ref.title + ' — ' + t.col.name"
              @click="desk.editTask('task', t.el.ref)"
            >
              {{ t.el.ref.title }}
            </button>
            <v-menu
              v-if="(byDay[d.key] || []).length > maxPerCell"
              offset-y
              max-height="300"
              :close-on-content-click="true"
            >
              <template v-slot:activator="{ on }">
                <button type="button" class="cv__more" v-on="on">
                  {{ $t('taskDeskMore', { n: byDay[d.key].length - maxPerCell }) }}
                </button>
              </template>
              <v-list dense>
                <v-list-item v-for="t in byDay[d.key]" :key="t.key" @click="desk.editTask('task', t.el.ref)">
                  <span class="cv__menu-dot" :style="{ background: t.col.color }" />
                  <v-list-item-title>{{ t.el.ref.title }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { today, dayKey, monthGrid, weekdayNames, monthTitle, addMonths } from '../deskDates'

// Tasks are placed on their due date (or start date when there is no due).
export function tasksByDay (tasks) {
  const map = {}
  tasks.forEach(t => {
    if (!t.end) return
    const k = dayKey(t.end)
    ;(map[k] = map[k] || []).push(t)
  })
  return map
}

export default {
  name: 'CalendarView',
  inject: ['desk'],
  data () {
    return { cursor: today(), maxPerCell: 3 }
  },
  computed: {
    weeks () {
      return monthGrid(this.cursor, this.desk.jalali)
    },
    weekdays () {
      return weekdayNames(this.desk.jalali)
    },
    title () {
      return monthTitle(this.cursor, this.desk.jalali)
    },
    byDay () {
      return tasksByDay(this.desk.tasks)
    },
    unscheduled () {
      return this.desk.tasks.filter(t => !t.end).length
    },
  },
  methods: {
    shift (n) {
      this.cursor = addMonths(this.cursor, n, this.desk.jalali)
    },
    goToday () {
      this.cursor = today()
    },
    isLate (t) {
      return !t.done && t.end.isBefore(today())
    },
  },
}
</script>

<style scoped>
.cv {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e6e9ef;
  border-radius: 8px;
  overflow: hidden;
}
.theme--dark .cv {
  background: #1e1e1e;
  border-color: #333;
}
.cv__bar {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-bottom: 1px solid #e6e9ef;
}
.theme--dark .cv__bar {
  border-color: #333;
}
.cv__nav {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid #dfe3ea;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.cv__nav:hover,
.cv__chip:hover {
  border-color: #b8c0cc;
}
.cv__title {
  font-weight: 700;
  font-size: 0.95rem;
  margin: 0 8px;
  min-width: 120px;
}
.cv__chip {
  height: 30px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid #dfe3ea;
  font-size: 0.78rem;
}
.cv__legend,
.cv__muted {
  font-size: 0.74rem;
  color: #8a94a6;
}
.cv__legend {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.cv__legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  box-shadow: inset 0 0 0 1px #e91e63;
}
.cv__week {
  flex: 0 0 auto;
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  border-bottom: 1px solid #e6e9ef;
  background: #f8f9fb;
}
.theme--dark .cv__week {
  background: #191919;
  border-color: #333;
}
.cv__week span {
  padding: 8px;
  font-size: 0.74rem;
  color: #8a94a6;
  text-align: center;
}
.cv__grid {
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  overflow-y: auto;
}
.cv__cell {
  min-height: 96px;
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid #eef0f4;
  border-inline-end: 1px solid #eef0f4;
  padding: 4px 4px 6px;
  min-width: 0;
}
.theme--dark .cv__cell {
  border-color: #2a2a2a;
}
.cv__cell:nth-child(7n) {
  border-inline-end: 0;
}
.cv__cell--we {
  background: rgba(194, 24, 91, 0.025);
}
.cv__cell--out {
  background: #fafbfc;
}
.cv__cell--out .cv__num {
  color: #c0c6cf;
}
.theme--dark .cv__cell--out {
  background: #1a1a1a;
}
.cv__cell-head {
  display: flex;
  padding: 2px 4px 4px;
}
.cv__num {
  min-width: 24px;
  height: 24px;
  line-height: 24px;
  border-radius: 12px;
  text-align: center;
  font-size: 0.78rem;
  color: #374151;
}
.theme--dark .cv__num {
  color: #ddd;
}
.cv__cell--today .cv__num {
  background: var(--v-primary-base);
  color: #fff;
  font-weight: 700;
}
.cv__events {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.cv__ev {
  display: block;
  width: 100%;
  text-align: start;
  font-size: 0.72rem;
  line-height: 20px;
  height: 22px;
  padding: 0 6px;
  padding-inline-start: 8px;
  border-radius: 4px;
  background: rgba(120, 130, 150, 0.1);
  background: color-mix(in srgb, var(--ec) 14%, transparent);
  border-inline-start: 3px solid var(--ec);
  color: #1f2937;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.theme--dark .cv__ev {
  color: #eee;
}
.cv__ev:hover {
  background: color-mix(in srgb, var(--ec) 24%, transparent);
}
.cv__ev--done {
  opacity: 0.55;
  text-decoration: line-through;
}
.cv__ev--late {
  box-shadow: inset 0 0 0 1px #e91e63;
}
.cv__more {
  font-size: 0.72rem;
  color: var(--v-primary-base);
  text-align: start;
  padding: 0 6px;
}
.cv__menu-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-inline-end: 10px;
}
</style>
